import { type CanvasElement, type WrapperRefs } from '@blaze-runtime/engine-domain';

import { hasProps, hasWrapperRefs } from './element-props-guards';

/**
 * The element's wrapper refs, for reading.
 *
 * An element whose props carry no `wrapper` gets an empty object that belongs to nobody — fine
 * for a reader, silent data loss for a writer. Writers take
 * {@link getOrCreateWrapperRefsMutably}.
 */
export function getWrapperRefs(element: CanvasElement | undefined): WrapperRefs {
  return hasWrapperRefs(element) ? element.props.wrapper.refs : {};
}

/**
 * The element's own wrapper refs, created if its props do not carry them yet, for a caller
 * that is going to write.
 *
 * Only a *generated* wrapper is born with `wrapper.refs`; one the builder drags off the
 * components list has no `wrapper` at all until something puts generated children inside it.
 * Reaching for {@link getWrapperRefs} to write into is what drops those first refs on the
 * floor, with no type error and no throw to say so.
 */
export function getOrCreateWrapperRefsMutably(element: CanvasElement): WrapperRefs {
  if (!hasWrapperRefs(element) && hasProps(element)) {
    element.props.wrapper = { refs: {} };
  }
  return getWrapperRefs(element);
}
